/** HEAD 조회(`GET /api/v1/return/work-scans/summary`) 요청 — 전부 필수(BE 400 가드). */
export interface ReturnWorkScanHeadFilter {
  shipper: string
  trsInvoiceNo: string
  actDateFrom: string
  actDateTo: string
}

/** HEAD 조회 응답 행 — `ReturnWorkScanHeadDto`. 화면이 쓰는 필드만 선언한다. */
export interface ReturnWorkScanHeadRes {
  sprNm: string | null
  ordNm: string | null
  trsNm: string | null
  trsPhone: string | null
  trsMobile: string | null
  trsAddr: string | null
  trsZip: string | null
  trsInvoiceNo: string | null
  /** 다음 반품번호(`max(ORD_NO) + 1`). 결과 첫 행에만 값이 실린다. */
  ordNoMax: number | null
}

/** ITEM 조회(`GET /api/v1/return/work-scans`) 요청. `flag=false` 일 때만 `trsInvoiceNo`·출고일자가 실제로 쓰인다. */
export interface ReturnWorkScanItemFilter {
  shipper: string
  barcode: string
  trsInvoiceNo: string
  actDateFrom: string
  actDateTo: string
  /** true 면 `master_item` 단독 조회(마스터 조회여부). 기본 false. */
  flag: boolean
}

/** ITEM 조회 응답 행 — `ReturnWorkScanItemDto` 18필드. */
export interface ReturnWorkScanItemRes {
  barcode: string | null
  skuId: string | null
  refNo1: string | null
  refNo2: string | null
  gdsCd: string | null
  gdsNm: string | null
  gdsOpt: string | null
  gdsColor: string | null
  gdsGroup1: string | null
  gdsAttr1: string | null
  stlNo: string | null
  brdNm: string | null
  ordQty: number | null
  salChannel: string | null
  trsNm: string | null
  trsPhone: string | null
  trsMobile: string | null
  trsAddr: string | null
}

/** 정불구분 — 서버 기본값도 '정상'이다(피드백 8). */
export const CONDITION_OPTIONS = [
  { label: '정상', value: '정상' },
  { label: '불량', value: '불량' },
] as const

export type ConditionValue = (typeof CONDITION_OPTIONS)[number]['value']

/** 그리드 한 행 — 스캔 결과를 화면 상태로 옮긴 모델. `key` 는 로컬 렌더용, 서버로 보내지 않는다. */
export interface ReturnWorkScanRow {
  key: string
  orderNumber: string
  barcode: string
  skuId: string
  goodsCode: string
  goodsName: string
  goodsOption: string
  goodsColor: string
  goodsGroup1: string
  goodsAttribute1: string
  styleNumber: string
  brandName: string
  orderQuantity: number
  salesChannel: string
  referenceNumber1: string
  referenceNumber2: string
  /** 정불구분 편집 셀. 미지정 시 서버가 '정상'으로 채운다. */
  condition: ConditionValue | ''
  /** 비고 편집 셀. */
  procurementName: string
  /** 중복 판정된 행 표시(6단계). */
  duplicate: boolean
  /**
   * 반품처 편집 셀(LOGISPRD-12384 BE 조사 결론). 조회 응답에 없는 이유는 DTO 누락이 아니라
   * 애초에 작업자가 채우는 값이기 때문이다 — 1.0 ITEM 스캔 쿼리(`RGR06_ITEM_SearchForm`)에도
   * RL_CHANNEL 이 없고, INSERT 는 그리드 행에서 그대로 읽어 반영한다.
   * 저장 계약(`ReturnWorkScanSaveItemReq`)에도 이 필드가 없다 — 값을 편집해도 서버로 가지 않는다.
   * 옵션 목록(콤보)은 `feature/LOGISPRD-12385-combo-options-api` 병행 작업 대상이라 이 브랜치에서는
   * 배선하지 않는다(자유입력 텍스트로만 연다).
   */
  rlChannel: string
  /** 반품택배(TRS_LSP_NM) 편집 셀. 저장 계약이 `logisticsProviderName` 으로 받는다. */
  logisticsProviderName: string
  /** 반품계좌(ETC1) 편집 셀. 저장 계약이 `etc1` 로 받는다. */
  etc1: string
  /** 반송금액(ETC2) 편집 셀. 저장 계약이 `etc2` 로 받는다(문자열, 서버가 숫자로 파싱). */
  etc2: string
  /** 착불금액(ETC3) 편집 셀. 저장 계약이 `etc3` 로 받는다(문자열, 서버가 숫자로 파싱). */
  etc3: string
  /** 선/착불(ETC4) 편집 셀. 저장 계약이 `etc4` 로 받는다. */
  etc4: string
  // 생성일자·생성자·수정일자·수정자 — 서버가 채운다(생성일시는 주입 Clock KST, 생성자는 인증
  // principal email). 요청에 실어도 서버가 무시하므로 저장 전 스캔 화면에서는 항상 빈칸이 맞다
  // (저장 후 재조회 경로가 없어 이번 화면은 셸 컬럼과 폭·순서만 맞춘다).
  insDate: string
  insBy: string
  updDate: string
  updBy: string
}

/** 운송장 패널 — HEAD 응답 또는 수기입력에서 채운다. */
export interface ReturnWorkScanHead {
  shipperCode: string
  returnInvoiceNumber: string
  originalInvoiceNumber: string
  orderName: string
  receiverName: string
  receiverPhone: string
  receiverAddress: string
  receiverZip: string
}

/** 저장(`POST /api/v1/return/work-scans/save`) 요청 바디. */
export interface ReturnWorkScanSaveHeadReq {
  shipperCode: string
  returnInvoiceNumber: string
  originalInvoiceNumber?: string
  receiverAddress?: string
  receiverPhone?: string
  receiverName?: string
  jobCommentForInvoice?: string
  isSendToDoosonERP: boolean
  fileId: string | null
}

export interface ReturnWorkScanSaveItemReq {
  orderNumber: string
  orderQuantity: number
  barcode: string
  goodsCode: string
  goodsName: string
  goodsOption: string
  goodsColor: string
  goodsGroup1: string
  goodsAttribute1: string
  brandName: string
  styleNumber: string
  salesChannel: string
  referenceNumber1: string
  referenceNumber2: string
  condition: ConditionValue | ''
  procurementName: string
  etc1: string
  etc2: string
  etc3: string
  etc4: string
  logisticsProviderName: string
  plannedDate?: string
}

export interface ReturnWorkScanSaveReq {
  head: ReturnWorkScanSaveHeadReq
  items: ReturnWorkScanSaveItemReq[]
}

export interface ReturnWorkScanSaveInsertedRes {
  id: number
  asnSequence: number
  orderNumber: string
  orderOptionNumber: string | null
}

export interface ReturnWorkScanSaveRes {
  inserted: ReturnWorkScanSaveInsertedRes[]
  duplicateOrderNumbers: string[]
}

/** 주문검색(`GET /api/v1/return/management/order-search`) 요청. */
export interface ReturnOrderSearchFilter {
  actDateFrom: string
  actDateTo: string
  shipper?: string
  barcode?: string[]
  refNo1?: string[]
  trsInvoiceNo?: string
  ordNm?: string
  trsNm?: string
  trsPhone?: string
  trsAddr?: string
}

/** 주문검색 응답 행 — `ReturnOrderSearchDto`. */
export interface ReturnOrderSearchRes {
  sprNm: string | null
  actDate: string | null
  trsInvoiceNo: string | null
  ordNm: string | null
  trsNm: string | null
  trsMobile: string | null
  trsPhone: string | null
  trsZip: string | null
  trsAddr: string | null
  barcode: string | null
  actQty: number | null
  refNo1: string | null
  salChannel: string | null
  brdNm: string | null
}

/** 파일 업로드 1단계(`POST /api/v1/common/file/uploads`) 요청. */
export interface FileUploadPresignReq {
  shipper: string
  fileName: string
  contentType: string
}

export interface FileUploadPresignRes {
  key: string
  uploadUrl: string
  signedHeaders: Record<string, string[]>
  expiresInSeconds: number
}

export type FileUploadResourceType = 'VIDEO' | 'ATCH_FILE'

/** 파일 업로드 3단계(`PATCH /api/v1/common/file/uploads`) 요청. */
export interface FileUploadCompleteReq {
  shipper: string
  key: string
  resourceType: FileUploadResourceType
  targetIds: number[]
}

export interface FileUploadCompleteRes {
  fileInfoId: number
  key: string
  linkedTargetCount: number
}
